import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Save, Settings as SettingsIcon, Building2, Percent, Users, FileText, 
  Calendar, RefreshCw, Link, Copy, Plus, Trash2, AlertTriangle, CheckCircle,
  ExternalLink, Clock, Wallet, Wrench, Sliders, LayoutGrid, Database,
  Upload, X, Image as ImageIcon, Loader2, ShoppingCart
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ConfigurationTab from '../components/ConfigurationTab';
import ModulesTab from '../components/ModulesTab';
import BackupTab from '../components/BackupTab';
import TenantCurrencySelector from '../components/TenantCurrencySelector';
import { useModules } from '../hooks/useModules';

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const { modules } = useModules();
  
  // Riad info state (from riads table - source of truth)
  const [riadInfo, setRiadInfo] = useState({
    id: null, name: '', address: '', city: '', email: '', phone: '', logo: null, logo_url: null
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Business config state (from ConfigOption - for additional settings like tax_id)
  const [businessInfo, setBusinessInfo] = useState({
    name: '', address: '', email: '', phone: '', tax_id: '', logo_url: ''
  });
  const [capacityConfig, setCapacityConfig] = useState({ max_pax: 10, max_rooms: 5 });
  
  // iCal state
  const [icalFeedUrl, setIcalFeedUrl] = useState('');
  const [icalUrls, setIcalUrls] = useState([]);
  const [newIcalUrl, setNewIcalUrl] = useState({ name: '', url: '' });
  const [icalStatus, setIcalStatus] = useState({ lastCheck: null, missingCount: 0, feedsConfigured: 0 });
  const [syncResult, setSyncResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [includeGuestName, setIncludeGuestName] = useState(true);
  const [availabilityWindow, setAvailabilityWindow] = useState(9);
  const [maxBookingDuration, setMaxBookingDuration] = useState(30);
  const [syncInterval, setSyncInterval] = useState(6);
  
  // HR Config state
  const [hrConfig, setHrConfig] = useState({
    work_hours_start: '08:00',
    work_hours_end: '17:00',
    late_threshold_minutes: 15,
    overtime_rate: 125,
    rest_days_expiry_months: 3,
    default_vacation_days: 18,
    default_leave_days_per_month: 1.5,
    default_rest_days_per_week: 1,
    default_hours_per_day: 8
  });
  
  // Channels state for payout delay
  const [channels, setChannels] = useState([]);
  
  // POS Config state
  const [posConfig, setPosConfig] = useState({
    free_product_enabled: false
  });

  // Handle URL params to open specific tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'ical') {
      setActiveTab('ical');
      // Auto-trigger sync when coming from reservations alert
      setTimeout(() => runSync(), 500);
    } else if (tab === 'config') {
      setActiveTab('config');
    } else if (tab === 'financial') {
      setActiveTab('financial');
    } else if (tab === 'invoice') {
      setActiveTab('invoice');
    } else if (tab === 'modules') {
      setActiveTab('modules');
    } else if (tab === 'backup') {
      setActiveTab('backup');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSettings();
    fetchRiadInfo();
    fetchBusinessConfig();
    fetchIcalSettings();
    fetchHRConfig();
    fetchChannels();
    fetchPosConfig();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setIncludeGuestName(response.data.ical_include_guest_name !== 'false');
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch riad info from riads table (source of truth for name and logo)
  const fetchRiadInfo = async () => {
    try {
      const response = await api.get('/settings/riad');
      setRiadInfo(response.data);
    } catch (error) {
      console.error('Error fetching riad info:', error);
    }
  };
  
  const fetchBusinessConfig = async () => {
    try {
      // Fetch business info from ConfigOption
      const businessRes = await api.get('/config/singleton/BUSINESS_INFO');
      if (businessRes.data.metadata) {
        setBusinessInfo(businessRes.data.metadata);
      }
      
      // Fetch capacity config
      const capacityRes = await api.get('/config/singleton/CAPACITY');
      if (capacityRes.data.metadata) {
        setCapacityConfig(capacityRes.data.metadata);
      }
    } catch (error) {
      console.error('Error fetching business config:', error);
    }
  };

  const fetchIcalSettings = async () => {
    try {
      // Get feed URL
      const feedRes = await api.get('/ical/feed-url');
      setIcalFeedUrl(feedRes.data.url);
      
      // Get status
      const statusRes = await api.get('/ical/status');
      setIcalStatus(statusRes.data);
      
      // Get availability window
      try {
        const windowRes = await api.get('/ical/availability-window');
        setAvailabilityWindow(windowRes.data.months || 9);
      } catch (e) {}
      
      // Get max booking duration
      try {
        const durationRes = await api.get('/ical/max-duration');
        setMaxBookingDuration(durationRes.data.days || 30);
      } catch (e) {}
      
      // Get sync interval
      try {
        const intervalRes = await api.get('/ical/sync-interval');
        setSyncInterval(intervalRes.data.hours || 6);
      } catch (e) {}
      
      // Get configured URLs from settings
      const settingsRes = await api.get('/settings');
      if (settingsRes.data.ical_import_urls) {
        try {
          setIcalUrls(JSON.parse(settingsRes.data.ical_import_urls));
        } catch (e) {}
      }
    } catch (error) {
      console.error('Error fetching iCal settings:', error);
    }
  };

  const fetchHRConfig = async () => {
    try {
      const res = await api.get('/config/singleton/HR_CONFIG');
      if (res.data?.metadata) {
        setHrConfig(prev => ({ ...prev, ...res.data.metadata }));
      }
    } catch (error) {
      console.error('Error fetching HR config:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      const res = await api.get('/config?category=CHANNEL');
      const channelData = res.data?.CHANNEL?.options || [];
      setChannels(channelData);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchPosConfig = async () => {
    try {
      const res = await api.get('/settings');
      const freeProductEnabled = res.data?.pos_free_product_enabled;
      setPosConfig({
        free_product_enabled: freeProductEnabled === true || freeProductEnabled === 'true'
      });
    } catch (error) {
      console.error('Error fetching POS config:', error);
    }
  };

  const savePosConfig = async () => {
    try {
      await api.post('/settings/bulk', {
        settings: {
          pos_free_product_enabled: { 
            value: String(posConfig.free_product_enabled), 
            type: 'BOOLEAN' 
          }
        }
      });
      toast.success('Configuration POS enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const updateSyncInterval = async (hours) => {
    try {
      await api.put('/ical/sync-interval', { hours: parseInt(hours) });
      setSyncInterval(hours);
      toast.success(`Intervalle de synchronisation mis à jour (${hours}h)`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateAvailabilityWindow = async (months) => {
    try {
      await api.put('/ical/availability-window', { months: parseInt(months) });
      setAvailabilityWindow(months);
      toast.success('Plage de disponibilité mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const updateMaxBookingDuration = async (days) => {
    try {
      await api.put('/ical/max-duration', { days: parseInt(days) });
      setMaxBookingDuration(days);
      toast.success('Durée max mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const saveHRConfig = async () => {
    try {
      await api.put('/config/singleton/HR_CONFIG', { metadata: hrConfig });
      toast.success('Configuration RH enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Save riad info (name, contact, etc.) - this updates riads table
  const saveRiadInfo = async () => {
    try {
      const response = await api.put('/settings/riad', {
        name: riadInfo.name,
        address: riadInfo.address,
        city: riadInfo.city,
        phone: riadInfo.phone,
        email: riadInfo.email
      });
      setRiadInfo(response.data.riad);
      toast.success('Informations du riad mises à jour');
      // Trigger a full reload to update the sidebar riad name
      window.location.reload();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Upload logo for the current riad
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non supporté. Utilisez JPG, PNG, GIF, WebP ou SVG.');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Taille max: 2 MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/settings/riad/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setRiadInfo(prev => ({
        ...prev,
        logo: response.data.logo,
        logo_url: response.data.logo_url
      }));
      
      toast.success('Logo uploadé avec succès');
      // Reload to update sidebar
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Delete logo
  const handleLogoDelete = async () => {
    if (!riadInfo.logo) return;
    
    try {
      await api.delete('/settings/riad/logo');
      setRiadInfo(prev => ({ ...prev, logo: null, logo_url: null }));
      toast.success('Logo supprimé');
      // Reload to update sidebar
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateChannelPayoutDelay = async (channelId, payoutDelay) => {
    try {
      const channel = channels.find(c => c.id === channelId);
      if (!channel) return;
      
      const updatedMetadata = {
        ...channel.metadata,
        payout_delay_days: parseInt(payoutDelay)
      };
      
      await api.put(`/config/${channelId}`, { metadata: updatedMetadata });
      
      setChannels(prev => prev.map(c => 
        c.id === channelId 
          ? { ...c, metadata: updatedMetadata }
          : c
      ));
      
      toast.success(`Délai de versement ${channel.label} mis à jour`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save business config to ConfigOption
      await api.put('/config/singleton/BUSINESS_INFO', { metadata: businessInfo });
      await api.put('/config/singleton/CAPACITY', { metadata: capacityConfig });
      
      // Save technical settings (iCal, maintenance) to Settings
      const settingsToSave = {
        ical_include_guest_name: { value: String(includeGuestName), type: 'STRING' },
        maintenance_mode: { value: settings.maintenance_mode || 'false', type: 'STRING' },
        maintenance_message: { value: settings.maintenance_message || '', type: 'STRING' }
      };

      await api.post('/settings/bulk', { settings: settingsToSave });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const regenerateFeedToken = async () => {
    try {
      const res = await api.post('/ical/regenerate-token');
      const feedRes = await api.get('/ical/feed-url');
      setIcalFeedUrl(feedRes.data.url);
      toast.success('Nouveau lien généré');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addIcalUrl = async () => {
    if (!newIcalUrl.name || !newIcalUrl.url) {
      toast.error('Nom et URL requis');
      return;
    }
    
    const updatedUrls = [...icalUrls, newIcalUrl];
    try {
      await api.post('/settings/bulk', {
        settings: {
          ical_import_urls: { value: JSON.stringify(updatedUrls), type: 'STRING' }
        }
      });
      setIcalUrls(updatedUrls);
      setNewIcalUrl({ name: '', url: '' });
      toast.success('URL ajoutée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const removeIcalUrl = async (index) => {
    const updatedUrls = icalUrls.filter((_, i) => i !== index);
    try {
      await api.post('/settings/bulk', {
        settings: {
          ical_import_urls: { value: JSON.stringify(updatedUrls), type: 'STRING' }
        }
      });
      setIcalUrls(updatedUrls);
      toast.success('URL supprimée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/ical/check');
      setSyncResult(res.data);
      setIcalStatus({
        lastCheck: res.data.lastCheck,
        missingCount: res.data.missing?.length || 0,
        feedsConfigured: res.data.feeds?.length || 0
      });
      toast.success('Synchronisation terminée');
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Paramètres</h1>
          <p className="text-muted-foreground mt-1">Configuration du système</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="general">
            <Building2 className="w-4 h-4 mr-2" />
            Général
          </TabsTrigger>
          <TabsTrigger value="modules">
            <LayoutGrid className="w-4 h-4 mr-2" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="config">
            <Sliders className="w-4 h-4 mr-2" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="business">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Règles métier
          </TabsTrigger>
          <TabsTrigger value="financial">
            <Percent className="w-4 h-4 mr-2" />
            Financier
          </TabsTrigger>
          <TabsTrigger value="invoice">
            <FileText className="w-4 h-4 mr-2" />
            Facturation
          </TabsTrigger>
          <TabsTrigger value="ical" disabled={modules.ical === false}>
            <Calendar className="w-4 h-4 mr-2" />
            Calendrier iCal
            {icalStatus.missingCount > 0 && (
              <Badge variant="destructive" className="ml-2">{icalStatus.missingCount}</Badge>
            )}
          </TabsTrigger>
          {modules.backup !== false && (
            <TabsTrigger value="backup">
              <Database className="w-4 h-4 mr-2" />
              Sauvegarde
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4">
          {/* Riad Identity Card - Source of Truth from riads table */}
          <Card data-testid="riad-identity-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Identité du Riad
              </CardTitle>
              <CardDescription>
                Ces informations sont affichées dans la sidebar et le sélecteur de riad
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Section */}
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <Label className="block mb-2">Logo du riad</Label>
                  <div className="relative">
                    {riadInfo.logo_url ? (
                      <div className="group relative">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${riadInfo.logo_url}`}
                          alt="Logo du riad"
                          className="w-24 h-24 object-contain rounded-lg border-2 border-gray-200 bg-white"
                          data-testid="riad-logo-preview"
                        />
                        <button
                          onClick={handleLogoDelete}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Supprimer le logo"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  <Label>Uploader un nouveau logo</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('logo-upload').click()}
                      disabled={uploadingLogo}
                      data-testid="logo-upload-button"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Upload en cours...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Choisir un fichier
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG, GIF, WebP ou SVG. Max 2 MB.
                  </p>
                </div>
              </div>

              {/* Riad Name and Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label>Nom du riad</Label>
                  <Input
                    value={riadInfo.name || ''}
                    onChange={(e) => setRiadInfo({ ...riadInfo, name: e.target.value })}
                    placeholder="Riad KAMA"
                    data-testid="riad-name-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ce nom est affiché partout dans l'application
                  </p>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={riadInfo.email || ''}
                    onChange={(e) => setRiadInfo({ ...riadInfo, email: e.target.value })}
                    placeholder="contact@riad.ma"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={riadInfo.phone || ''}
                    onChange={(e) => setRiadInfo({ ...riadInfo, phone: e.target.value })}
                    placeholder="+212 5 24 XX XX XX"
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={riadInfo.city || ''}
                    onChange={(e) => setRiadInfo({ ...riadInfo, city: e.target.value })}
                    placeholder="Marrakech"
                  />
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Input
                  value={riadInfo.address || ''}
                  onChange={(e) => setRiadInfo({ ...riadInfo, address: e.target.value })}
                  placeholder="Derb ..., Médina"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={saveRiadInfo} data-testid="save-riad-info-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les infos du riad
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Config - Separate card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Configuration de capacité
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Capacité max (personnes)</Label>
                  <Input
                    type="number"
                    value={capacityConfig.max_pax || 10}
                    onChange={(e) => setCapacityConfig({ ...capacityConfig, max_pax: parseInt(e.target.value) || 10 })}
                  />
                </div>
                <div>
                  <Label>Nombre de chambres</Label>
                  <Input
                    type="number"
                    value={capacityConfig.max_rooms || 5}
                    onChange={(e) => setCapacityConfig({ ...capacityConfig, max_rooms: parseInt(e.target.value) || 5 })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Info for documents/invoices */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Informations légales (pour documents)
              </CardTitle>
              <CardDescription>
                Ces informations apparaissent sur les factures et documents officiels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Numéro de taxe / ICE</Label>
                  <Input
                    value={businessInfo.tax_id || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, tax_id: e.target.value })}
                    placeholder="ICE: 000123456000012"
                  />
                </div>
                <div>
                  <Label>URL du logo (pour factures/pré check-in)</Label>
                  <Input
                    value={businessInfo.logo_url || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, logo_url: e.target.value })}
                    placeholder="https://exemple.com/logo.png"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Optionnel: utilisez une URL externe si le logo interne ne fonctionne pas
                  </p>
                </div>
              </div>
              {businessInfo.logo_url && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <img 
                    src={businessInfo.logo_url} 
                    alt="Logo preview" 
                    className="h-12 w-12 object-contain rounded-lg border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <span className="text-sm text-muted-foreground">Aperçu du logo URL</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Maintenance Mode */}
          <Card data-testid="maintenance-mode-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Mode maintenance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Activez le mode maintenance pour bloquer temporairement l'accès au site pendant des travaux ou une mise à jour.
              </p>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label className="font-medium text-amber-800">Mode maintenance actif</Label>
                  <p className="text-xs text-amber-700 mt-1">
                    Seuls les propriétaires pourront accéder à l'application
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={settings.maintenance_mode === 'true' || settings.maintenance_mode === true}
                    onCheckedChange={(checked) => setSettings({ ...settings, maintenance_mode: String(checked) })}
                    data-testid="maintenance-mode-switch"
                  />
                  <span className={`text-sm font-medium min-w-[80px] ${(settings.maintenance_mode === 'true' || settings.maintenance_mode === true) ? 'text-amber-700' : 'text-gray-500'}`}>
                    {(settings.maintenance_mode === 'true' || settings.maintenance_mode === true) 
                      ? 'Activé'
                      : 'Désactivé'}
                  </span>
                </div>
              </div>
              {(settings.maintenance_mode === 'true' || settings.maintenance_mode === true) && (
                <div>
                  <Label>Message de maintenance (optionnel)</Label>
                  <Input
                    value={settings.maintenance_message || ''}
                    onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                    placeholder="Le site est en maintenance, nous revenons bientôt..."
                    data-testid="maintenance-message"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab - Listes et options métier */}
        <TabsContent value="config" className="space-y-4">
          <ConfigurationTab />
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          <ModulesTab />
        </TabsContent>

        {/* Backup Tab */}
        {modules.backup !== false && (
          <TabsContent value="backup" className="space-y-4">
            <BackupTab />
          </TabsContent>
        )}

        {/* Business Rules Tab - HR & Channels */}
        <TabsContent value="business" className="space-y-4">
          {/* POS Configuration */}
          <Card data-testid="pos-config-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Configuration Ventes (POS)
                </div>
                <Button onClick={savePosConfig} size="sm" variant="outline" data-testid="save-pos-config-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer POS
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="font-medium">Produit libre</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Permet d'ajouter des produits avec un nom et prix personnalisés lors d'une vente.
                    Utile pour les ventes exceptionnelles non référencées dans le catalogue.
                  </p>
                </div>
                <Switch
                  checked={posConfig.free_product_enabled}
                  onCheckedChange={(checked) => setPosConfig(prev => ({ ...prev, free_product_enabled: checked }))}
                  data-testid="pos-free-product-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Channels - Payout Delays */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Délais de versement OTA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Configurez le nombre de jours après le départ du client pour recevoir le versement de chaque plateforme.
              </p>
              <div className="grid gap-4">
                {channels.filter(c => c.metadata?.is_ota).map(channel => {
                  const meta = typeof channel.metadata === 'string' 
                    ? JSON.parse(channel.metadata) 
                    : channel.metadata || {};
                  return (
                    <div key={channel.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: channel.color || meta.color || '#6B7280' }}
                        />
                        <div>
                          <p className="font-medium">{channel.label}</p>
                          <p className="text-sm text-muted-foreground">
                            Commission: {meta.commission_rate || 0}%
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-muted-foreground">Délai versement:</Label>
                        <Select 
                          value={String(meta.payout_delay_days ?? 7)} 
                          onValueChange={(v) => updateChannelPayoutDelay(channel.id, v)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[0, 3, 5, 7, 10, 14, 21, 30].map(d => (
                              <SelectItem key={d} value={String(d)}>J+{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
                {channels.filter(c => c.metadata?.is_ota).length === 0 && (
                  <p className="text-sm text-muted-foreground italic">
                    Aucun canal OTA configuré. Ajoutez des canaux dans l'onglet Configuration → Réservations → Canaux.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* HR Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Configuration RH
                </div>
                <Button onClick={saveHRConfig} size="sm" variant="outline">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer RH
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Horaires de travail */}
              <div>
                <h4 className="font-medium mb-3">Horaires de travail</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Début journée</Label>
                    <Input
                      type="time"
                      value={hrConfig.work_hours_start}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, work_hours_start: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Fin journée</Label>
                    <Input
                      type="time"
                      value={hrConfig.work_hours_end}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, work_hours_end: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Heures/jour par défaut</Label>
                    <Input
                      type="number"
                      min="1"
                      max="24"
                      value={hrConfig.default_hours_per_day}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, default_hours_per_day: parseInt(e.target.value) || 8 }))}
                    />
                  </div>
                </div>
              </div>

              {/* Règles de présence */}
              <div>
                <h4 className="font-medium mb-3">Règles de présence</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Seuil de retard (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      value={hrConfig.late_threshold_minutes}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, late_threshold_minutes: parseInt(e.target.value) || 15 }))}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Au-delà de ce délai, l'employé est considéré en retard
                    </p>
                  </div>
                  <div>
                    <Label>Taux heures supplémentaires (%)</Label>
                    <Input
                      type="number"
                      min="100"
                      max="300"
                      value={hrConfig.overtime_rate}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, overtime_rate: parseInt(e.target.value) || 125 }))}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Majoration appliquée aux heures sup (ex: 125 = +25%)
                    </p>
                  </div>
                </div>
              </div>

              {/* Congés */}
              <div>
                <h4 className="font-medium mb-3">Congés et repos</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label>Congés annuels (jours)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="60"
                      value={hrConfig.default_vacation_days}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, default_vacation_days: parseInt(e.target.value) || 18 }))}
                    />
                  </div>
                  <div>
                    <Label>Congés mensuels acquis</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="5"
                      value={hrConfig.default_leave_days_per_month}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, default_leave_days_per_month: parseFloat(e.target.value) || 1.5 }))}
                    />
                  </div>
                  <div>
                    <Label>Jours repos/semaine</Label>
                    <Input
                      type="number"
                      min="0"
                      max="7"
                      value={hrConfig.default_rest_days_per_week}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, default_rest_days_per_week: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div>
                    <Label>Expiration repos (mois)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={hrConfig.rest_days_expiry_months}
                      onChange={(e) => setHrConfig(prev => ({ ...prev, rest_days_expiry_months: parseInt(e.target.value) || 3 }))}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Durée avant expiration des jours de repos non pris
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4">
          {/* Devise principale du tenant */}
          <TenantCurrencySelector />
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5" />
                Parametres financiers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Info sur la configuration centralisee */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 mb-3">
                  <strong>Configuration centralisee :</strong> Les taux de change et la liste des devises 
                  sont geres au niveau plateforme par l'administrateur.
                </p>
                <div className="space-y-2 text-sm text-blue-700">
                  <p>Votre devise principale (ci-dessus) determine la devise par defaut pour:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>Les factures et devis</li>
                    <li>Les rapports financiers</li>
                    <li>L'affichage des prix</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tourist Tax Info */}
          <Card data-testid="tourist-tax-settings">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Taxes de sejour
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Configuration centralisee :</strong> La taxe de sejour est maintenant geree depuis{' '}
                  <strong>l'onglet Configuration &gt; Taxe de sejour</strong> ou vous pouvez definir :
                </p>
                <ul className="text-sm text-amber-700 mt-2 ml-4 list-disc space-y-1">
                  <li>Le montant et la devise</li>
                  <li>L'application par nuit et/ou par personne</li>
                  <li>Le flux financier de destination</li>
                  <li>L'activation pour les reservations directes ou OTA</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Tab */}
        <TabsContent value="invoice" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Paramètres de facturation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom de l'entreprise</Label>
                  <Input
                    value={settings.company_name || ''}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>ICE / Identifiant fiscal</Label>
                  <Input
                    value={settings.tax_id || ''}
                    onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Téléphone facturation</Label>
                  <Input
                    value={settings.company_phone || ''}
                    onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email facturation</Label>
                  <Input
                    type="email"
                    value={settings.company_email || ''}
                    onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Adresse complète</Label>
                <Input
                  value={settings.company_address || ''}
                  onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                />
              </div>
              <div>
                <Label>URL du logo</Label>
                <Input
                  value={settings.logo_url || ''}
                  onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                />
              </div>
              <div>
                <Label>Mentions légales (pied de facture)</Label>
                <Textarea
                  value={settings.invoice_footer || ''}
                  onChange={(e) => setSettings({ ...settings, invoice_footer: e.target.value })}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* iCal Tab */}
        <TabsContent value="ical" className="space-y-4">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  Statut de synchronisation
                </div>
                <Button onClick={runSync} disabled={syncing} variant="outline">
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Vérification...' : 'Vérifier maintenant'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Dernière vérification</p>
                  <p className="font-medium">
                    {icalStatus.lastCheck 
                      ? format(new Date(icalStatus.lastCheck), 'dd MMM yyyy HH:mm', { locale: fr })
                      : 'Jamais'
                    }
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Flux configurés</p>
                  <p className="font-medium">{icalStatus.feedsConfigured}</p>
                </div>
                <div className={`p-4 rounded-lg ${icalStatus.missingCount > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className="text-sm text-muted-foreground">Réservations manquantes</p>
                  <p className={`font-medium text-xl ${icalStatus.missingCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {icalStatus.missingCount}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800">Intervalle de synchronisation :</span>
                <Select value={String(syncInterval)} onValueChange={(v) => updateSyncInterval(parseInt(v))}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Toutes les heures</SelectItem>
                    <SelectItem value="2">Toutes les 2h</SelectItem>
                    <SelectItem value="4">Toutes les 4h</SelectItem>
                    <SelectItem value="6">Toutes les 6h</SelectItem>
                    <SelectItem value="8">Toutes les 8h</SelectItem>
                    <SelectItem value="12">Toutes les 12h</SelectItem>
                    <SelectItem value="24">Une fois par jour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Availability Window */}
          <Card>
            <CardHeader>
              <CardTitle>Plage de disponibilité</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Définissez jusqu'à combien de mois à l'avance vous acceptez des réservations.
                Les dates bloquées au-delà de cette plage dans les calendriers externes seront ignorées.
              </p>
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-3">
                  <Label>Fenêtre de disponibilité</Label>
                  <Select 
                    value={String(availabilityWindow)} 
                    onValueChange={(v) => updateAvailabilityWindow(parseInt(v))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 6, 9, 12, 15, 18, 24].map(m => (
                        <SelectItem key={m} value={String(m)}>{m} mois</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <Label>Durée max d'une réservation</Label>
                  <Select 
                    value={String(maxBookingDuration)} 
                    onValueChange={(v) => updateMaxBookingDuration(parseInt(v))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[7, 14, 21, 30, 45, 60, 90].map(d => (
                        <SelectItem key={d} value={String(d)}>{d} jours</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Les blocages de calendrier plus longs que la durée max sont ignorés (fermetures de calendrier).
              </p>
            </CardContent>
          </Card>

          {/* Info about missing reservations */}
          {icalStatus.missingCount > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-800">
                      {icalStatus.missingCount} réservation{icalStatus.missingCount > 1 ? 's' : ''} manquante{icalStatus.missingCount > 1 ? 's' : ''}
                    </span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.location.href = '/reservations'}
                    className="bg-white"
                  >
                    Voir dans Réservations
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Feed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5" />
                Flux sortant (Export)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Utilisez ce lien pour synchroniser vos réservations avec Airbnb, Booking, ou votre calendrier personnel (iPhone, Outlook, Google Calendar).
              </p>
              
              <div className="flex items-center gap-2">
                <Input value={icalFeedUrl} readOnly className="font-mono text-sm" />
                <Button variant="outline" onClick={() => copyToClipboard(icalFeedUrl)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={includeGuestName}
                    onCheckedChange={setIncludeGuestName}
                  />
                  <Label>Inclure le nom du client dans les événements</Label>
                </div>
                <Button variant="ghost" size="sm" onClick={regenerateFeedToken}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Régénérer le lien
                </Button>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-800">Comment utiliser ce lien ?</p>
                <ul className="text-blue-700 mt-2 space-y-1">
                  <li>• <strong>Airbnb</strong>: Calendrier → Paramètres → Importer un calendrier</li>
                  <li>• <strong>Booking</strong>: Calendrier → Synchronisation → Ajouter connexion</li>
                  <li>• <strong>iPhone/iCloud</strong>: Réglages → Calendrier → Comptes → Ajouter → Autre → S'abonner</li>
                  <li>• <strong>Google Calendar</strong>: Autres agendas → À partir de l'URL</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Import Feeds */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-5 h-5" />
                Flux entrants (Import)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Ajoutez les liens iCal de vos plateformes pour détecter automatiquement les nouvelles réservations.
              </p>

              {/* Existing URLs */}
              {icalUrls.length > 0 && (
                <div className="space-y-2">
                  {icalUrls.map((feed, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{feed.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{feed.url}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeIcalUrl(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new URL */}
              <div className="border rounded-lg p-4 space-y-3">
                <p className="font-medium">Ajouter un flux</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    placeholder="Nom (ex: Airbnb)"
                    value={newIcalUrl.name}
                    onChange={(e) => setNewIcalUrl({ ...newIcalUrl, name: e.target.value })}
                  />
                  <Input
                    placeholder="URL iCal"
                    value={newIcalUrl.url}
                    onChange={(e) => setNewIcalUrl({ ...newIcalUrl, url: e.target.value })}
                    className="md:col-span-2"
                  />
                </div>
                <Button onClick={addIcalUrl} disabled={!newIcalUrl.name || !newIcalUrl.url}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg text-sm">
                <p className="font-medium text-amber-800">Où trouver les liens iCal ?</p>
                <ul className="text-amber-700 mt-2 space-y-1">
                  <li>• <strong>Airbnb</strong>: Calendrier → Paramètres → Exporter le calendrier</li>
                  <li>• <strong>Booking</strong>: Calendrier → Synchronisation → Exporter</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
